/**
 * Things the ranger does with his hands that are not shooting: first aid, arming a time
 * bomb, looting the ammo shack (01 §3), dragging a body out of sight (02 §6.2), and - in
 * `fieldcraft.ts` - cutting wire and going through a dead man's pouches.
 */

import type { Ctx } from '../ctx';
import { destroyProp } from '../damage';
import { isAutomated } from '../enemies/specials/kinds';
import { cos, sin } from '../math';
import { PROPS } from '../props';
import { resolveMovement } from '../statics';
import { podInReach } from '../insertion';
import { type ObjectiveInteract, objectiveInteractOption, performObjectiveInteract } from '../objectives/interactions';
import { stepUseAction } from '../objectives/useAction';
import { BOMB, DRAG, GEAR, RANGER, secs } from '../tuning';
import type { Enemy } from '../types';
import { salvage, salvageYield, wireInReach } from './fieldcraft';
import { openPod } from './pods';

/** What pressing Interact would do right now; the HUD shows it as a prompt. */
export type InteractOption =
  | { kind: 'drop' }
  | { kind: 'drag'; enemy: Enemy }
  | { kind: 'loot'; propId: number }
  | { kind: 'pod'; index: number }
  /** Wire cutters (04 §6): start the four-second, silent cut of this wire run. */
  | { kind: 'cut'; propId: number }
  /** Salvager (04 §4): take this body's one magazine or grenade. Offered once per body, before `drag`. */
  | { kind: 'salvage'; enemy: Enemy; what: 'magazine' | 'grenade' }
  // OBJ: 'use' (radio, lever), 'capture' (the officer), 'free' (a captive), 'uniform' (a dead man's), 'tags';
  // expansion: 'useObject' (hold-still work), 'lootBody', 'deliver', 'meet'.
  | ObjectiveInteract
  | null;

export function interactOption(ctx: Pick<Ctx, 'world' | 'statics'>): InteractOption {
  const { world: w, statics } = ctx;
  const r = w.ranger;
  if (r.status !== 'active') return null;
  // Both hands are on the cutters - or on the safe, the tap, the breaker.
  if (r.action === 'cutWire' || r.action === 'useObject') return null;
  if (r.dragging >= 0) return { kind: 'drop' };

  const pod = podInReach(ctx);
  if (pod) return { kind: 'pod', index: pod.index };

  // OBJ: what the mission itself wants done here comes before the housekeeping.
  const special = objectiveInteractOption(ctx);
  if (special) return special;

  let best: InteractOption = null;
  let bestD = Infinity;
  for (const e of w.enemies) {
    if (e.state !== 'dead' && e.state !== 'wounded') continue;
    // A wreck is not a body: nobody hauls a minitank into the bushes, or goes through its pockets.
    if (isAutomated(e.kind)) continue;
    const d = (e.x - r.x) * (e.x - r.x) + (e.z - r.z) * (e.z - r.z);
    if (d <= DRAG.reach * DRAG.reach && d < bestD) {
      bestD = d;
      // R6: "fallen enemies" is what the card says, and a man down and out of the fight has
      // fallen - the ranger can already haul him into the bushes, so his pouches are no stranger.
      const pouches = w.mods.salvager && !e.salvaged;
      best = pouches ? { kind: 'salvage', enemy: e, what: salvageYield(e) } : { kind: 'drag', enemy: e };
    }
  }
  for (const def of statics.def.props) {
    if (PROPS[def.kind].lootable !== true) continue;
    const state = w.props.find((p) => p.id === def.id);
    if (!state || state.destroyed || state.looted) continue;
    const reach = RANGER.lootRadius + 2.3;
    const d = (def.x - r.x) * (def.x - r.x) + (def.z - r.z) * (def.z - r.z);
    if (d <= reach * reach && d < bestD) {
      bestD = d;
      best = { kind: 'loot', propId: def.id };
    }
  }
  if (r.gear.wireCutters && r.action === 'none') {
    const wire = wireInReach(ctx);
    if (wire && wire.distance * wire.distance < bestD) best = { kind: 'cut', propId: wire.prop.id };
  }
  return best;
}

function releaseBody(ctx: Ctx): void {
  const r = ctx.world.ranger;
  const body = ctx.world.enemies.find((e) => e.id === r.dragging);
  if (body) body.draggedByRanger = false;
  r.dragging = -1;
}

/** Interact was pressed this tick: drop, drag, pod, pouches, cutters, loot - or the mission's own business. */
function performInteract(ctx: Ctx, option: NonNullable<InteractOption>): void {
  const r = ctx.world.ranger;
  switch (option.kind) {
    case 'drop':
      releaseBody(ctx);
      break;
    case 'drag':
      r.dragging = option.enemy.id;
      option.enemy.draggedByRanger = true;
      // Whoever was coming for him gives up: the casualty is no longer where he fell.
      option.enemy.carriedBy = -1;
      break;
    case 'pod':
      openPod(ctx, option.index);
      break;
    case 'salvage':
      salvage(ctx, option.enemy);
      break;
    case 'cut':
      // Timed like first aid: `movement.ts` cancels it the moment he moves, and a hit knocks it out of his hands.
      r.action = 'cutWire';
      r.actionTicks = 0;
      break;
    case 'loot': {
      const state = ctx.world.props.find((p) => p.id === option.propId);
      if (state) state.looted = true;
      r.inventory.magazines += 3;
      r.inventory.grenades += 2;
      ctx.events.push({ type: 'pickup', what: '+3 MAGAZINES  +2 GRENADES' });
      break;
    }
    default:
      performObjectiveInteract(ctx, option); // OBJ: radio, lever, officer, captives, uniform, dog tags
  }
}

/** A dragged body trails behind the ranger. */
function stepDrag(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const r = w.ranger;
  const body = w.enemies.find((e) => e.id === r.dragging);
  if (!body || r.status !== 'active' || r.staggerTicks > 0) {
    releaseBody(ctx);
    return;
  }
  const dx = body.x - r.x;
  const dz = body.z - r.z;
  const d = Math.sqrt(dx * dx + dz * dz);
  if (d > DRAG.trail) {
    const pulled = resolveMovement(statics, r.x + (dx / d) * DRAG.trail, r.z + (dz / d) * DRAG.trail, 0.25);
    body.prevX = body.x;
    body.prevZ = body.z;
    body.x = pulled.x;
    body.z = pulled.z;
  }
  // A body that has been moved is a fresh discovery for whoever comes across it.
  body.found = false;
}

export function stepActions(ctx: Ctx): void {
  const { world: w, cmd } = ctx;
  const r = w.ranger;

  if (cmd.interact && !r.prevInteract) {
    const option = interactOption(ctx);
    if (option) performInteract(ctx, option);
  }
  if (r.dragging >= 0) stepDrag(ctx);

  /* ---- first aid */
  if (cmd.firstAid && !r.prevFirstAid && r.action === 'none' && r.inventory.firstAid > 0 && r.wounds > 0) {
    if (r.dragging >= 0) releaseBody(ctx);
    r.action = 'firstAid';
    r.actionTicks = 0;
  }

  if (r.action === 'firstAid') {
    r.actionTicks++;
    if (r.actionTicks >= RANGER.firstAidTicks * w.mods.firstAidTime) {
      r.inventory.firstAid--;
      r.wounds = Math.max(0, r.wounds - 1 - w.mods.extraFirstAidHeal);
      if (r.status === 'downed') {
        r.status = 'active';
        r.bleedTicks = 0;
      }
      r.action = 'none';
      r.actionTicks = 0;
      ctx.events.push({ type: 'pickup', what: 'WOUND TREATED' });
    }
  } else if (r.action === 'placeBomb') {
    r.actionTicks++;
    if (r.actionTicks >= BOMB.placeTicks * w.mods.bombPlaceTime) {
      r.inventory.bombs--;
      w.bombs.push({ id: w.nextId++, x: r.x + sin(r.aim) * 0.5, z: r.z + cos(r.aim) * 0.5, fuseTicks: secs(cmd.fuse) });
      ctx.events.push({ type: 'bombPlaced', x: r.x, z: r.z, fuse: cmd.fuse });
      r.action = 'none';
      r.actionTicks = 0;
      r.weapon = 'carbine';
    }
  } else if (r.action === 'cutWire') {
    r.actionTicks++;
    if (r.actionTicks >= GEAR.cutTicks) {
      // The run he is still standing at (moving would have cancelled it). No wound, no noise - just a gap.
      const wire = wireInReach(ctx);
      if (wire) {
        destroyProp(ctx, wire.prop.id);
        ctx.events.push({ type: 'pickup', what: 'WIRE CUT' });
      }
      r.action = 'none';
      r.actionTicks = 0;
    }
  } else if (r.action === 'useObject') stepUseAction(ctx); // OBJ: the hold-still work at a `useObjects` target
}
